import { useRef, type MutableRefObject } from 'react';

import { useCallbackOne } from 'use-memo-one';

/**
 * Snapshots the method names in `ref` but resolves each implementation from `ref.current` at call
 * time. `undefined` when empty, so `consolidateCustomBridge` keeps its "no custom methods" fast path.
 */
const toLiveMethodMap = <M extends (...args: any[]) => any>(
	ref: MutableRefObject<Record<string, M> | undefined>,
): Record<string, M> | undefined => {
	const current = ref.current;
	if (!current) {
		return undefined;
	}
	return Object.keys(current).reduce(
		(acc, methodName) => {
			acc[methodName] = ((...args: Parameters<M>) => ref.current?.[methodName]?.(...args)) as M;
			return acc;
		},
		{} as Record<string, M>,
	);
};

/**
 * Returns a stable getter over a product-provided bridge method map that tracks the latest reference.
 *
 * Some modules recreate their methods after initial render (e.g. closures capturing fresh data);
 * capturing once at mount would freeze those. Reading `ref.current` at call time lets updates take
 * effect without re-opening the bridge — like base methods staying current via `getBridgeContext()`.
 *
 * For the legacy (v1) `customBridgeMethods` only. The v2 `customBridge` is a stable-reference
 * contract and is passed to `consolidateCustomBridge` directly.
 */
export const useLiveMethodMap = <M extends (...args: any[]) => any>(
	methods: Record<string, M> | undefined,
): (() => Record<string, M> | undefined) => {
	const ref = useRef(methods);
	ref.current = methods; // keep in sync with the latest props every render

	// Stable identity: the liveness lives in the wrappers, so the getter never needs to change.
	return useCallbackOne(() => toLiveMethodMap(ref), []);
};
